import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import {
    catchErrorLogRet,
    mapList,
    mapRetrieve,
} from '../../common/rest.operators';
import { getActualTrendColor } from '../_common';
import {
    EffectTypes,
    PeriodType,
    ScorecardBoxDto,
    ScorecardDataDto,
} from '../scorecard.model';

@Injectable()
export class ScorecardService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    listData(
        scorecardIds: number[],
        useLiveData?: boolean,
    ): Observable<ScorecardDataDto[]> {
        let params = new HttpParams();
        // eslint-disable-next-line no-return-assign
        scorecardIds.forEach(
            (e) => (params = params.append('scorecardIds', e.toString())),
        );
        let headers = new HttpHeaders();
        if (useLiveData) {
            headers = headers
                .set('Pragma', 'no-cache')
                .set('Cache-Control', 'no-cache');
        }
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.SCORECARD}/scorecards/data`, {
                headers,
                params,
            })
            .pipe(
                mapList(),
                tap((data: ScorecardDataDto[]) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    data &&
                        data.forEach((scData) => {
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            scData.fyiMetrics &&
                                scData.fyiMetrics.forEach((fyi) => {
                                    fyi.actualTrendColor = getActualTrendColor(
                                        fyi.actualTrend,
                                        fyi.rankOrder,
                                    );
                                });

                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            scData.compensationConditions &&
                                scData.compensationConditions.forEach((cc) => {
                                    switch (cc.effectType) {
                                        case EffectTypes.CAP:
                                        case EffectTypes.REDUCE_Pct:
                                        case EffectTypes.REDUCE_$:
                                            cc.isPositive = !cc.inEffect;
                                            break;
                                        default:
                                            cc.isPositive = cc.inEffect;
                                            break;
                                    }
                                });

                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            scData.compensationModifiers &&
                                scData.compensationModifiers.forEach((cc) => {
                                    switch (cc.effectType) {
                                        case EffectTypes.CAP:
                                        case EffectTypes.REDUCE_Pct:
                                        case EffectTypes.REDUCE_$:
                                            cc.isPositive = !cc.inEffect;
                                            break;
                                        default:
                                            cc.isPositive = cc.inEffect;
                                            break;
                                    }
                                });
                        });
                }),
                catchErrorLogRet([]),
            );
    }

    retrieveBox(
        userId: number,
        periodType?: PeriodType,
        scorecardId?: number,
        groupId?: number,
        endDate?: Date,
        useLiveData?: boolean,
    ): Observable<ScorecardBoxDto> {
        let params = new HttpParams().set('userId', userId.toString());
        if (periodType != null) {
            params = params.set('periodType', periodType.toString());
        }
        if (scorecardId != null) {
            params = params.set('scorecardId', scorecardId.toString());
        }
        if (groupId != null) {
            params = params.set('groupId', groupId.toString());
        }
        if (endDate != null) {
            params = params.set(
                'endDate',
                `${endDate.getFullYear()}-${
                    endDate.getMonth() + 1
                }-${endDate.getDate()}`,
            );
        }
        let headers = new HttpHeaders();
        if (useLiveData) {
            headers = headers
                .set('Pragma', 'no-cache')
                .set('Cache-Control', 'no-cache');
        }
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.SCORECARD}/scorecards/box`, {
                headers,
                params,
            })
            .pipe(
                mapRetrieve(),
                tap((data: ScorecardBoxDto) => {
                    if (data) {
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        data.scorecardHistory &&
                            data.scorecardHistory.forEach((x) => {
                                x.startDate = new Date(x.startDate);
                                x.endDate = new Date(x.endDate);
                                x.adjustedPoints = x.adjustedPoints || 0;
                            });

                        if (data.initialScorecard) {
                            data.initialScorecard.startDate = new Date(
                                data.initialScorecard.startDate,
                            );
                            data.initialScorecard.endDate = new Date(
                                data.initialScorecard.endDate,
                            );
                            data.initialScorecard.adjustedPoints =
                                data.initialScorecard.adjustedPoints || 0;
                        }

                        if (data.initialScorecardData) {
                            if (data.initialScorecardData.fyiMetrics) {
                                data.initialScorecardData.fyiMetrics.forEach(
                                    (fyi) => {
                                        fyi.actualTrendColor =
                                            getActualTrendColor(
                                                fyi.actualTrend,
                                                fyi.rankOrder,
                                            );
                                    },
                                );
                            }

                            if (
                                data.initialScorecardData.compensationConditions
                            ) {
                                data.initialScorecardData.compensationConditions.forEach(
                                    (cc) => {
                                        switch (cc.effectType) {
                                            case EffectTypes.CAP:
                                            case EffectTypes.REDUCE_Pct:
                                            case EffectTypes.REDUCE_$:
                                                cc.isPositive = !cc.inEffect;
                                                break;
                                            default:
                                                cc.isPositive = cc.inEffect;
                                                break;
                                        }
                                    },
                                );
                            }

                            if (
                                data.initialScorecardData.compensationModifiers
                            ) {
                                data.initialScorecardData.compensationModifiers.forEach(
                                    (cm) => {
                                        switch (cm.effectType) {
                                            case EffectTypes.CAP:
                                            case EffectTypes.REDUCE_Pct:
                                            case EffectTypes.REDUCE_$:
                                                cm.isPositive = !cm.inEffect;
                                                break;
                                            default:
                                                cm.isPositive = cm.inEffect;
                                                break;
                                        }
                                    },
                                );
                            }
                        }
                    }
                }),
                catchErrorLogRet(null),
            );
    }
}
